import React, { useState, useEffect, useRef } from 'react';
import { istDateTime, istDate, istTime } from '../lib/ist';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ScrollSentinel from '../components/ScrollSentinel';
import { 
  ArrowLeft, Monitor, MapPin, Wifi, WifiOff, Settings, 
  Palette, Grid, DollarSign, Package, RefreshCw, Plus, 
  Trash2, Copy, Download, AlertTriangle, Check, Power, Key, Zap, Printer, Save, Activity, Image, Clock, ListVideo, Camera, Upload, X
} from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth, canEdit } from '../App';
import SimpleTemplateEditor from '../components/SimpleTemplateEditor';

const KioskDetail = () => {
  const { kioskId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const editable = canEdit(user, 'booths');
  const [kiosk, setKiosk] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('template');
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);
  const [editPricing, setEditPricing] = useState(null);
  const [editUi, setEditUi] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  
  // New state for real-time management
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [printLogs, setPrintLogs] = useState([]);
  const [printLogsTotal, setPrintLogsTotal] = useState(0);
  const [printLogsPage, setPrintLogsPage] = useState(1);
  const [printLogsStatus, setPrintLogsStatus] = useState('');
  const [printLogsHasMore, setPrintLogsHasMore] = useState(false);
  const PRINT_LOGS_PAGE_SIZE = 20;

  // Inventory adjustments
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustType, setAdjustType] = useState('waste');
  const [adjustDelta, setAdjustDelta] = useState(1);
  const [adjustReason, setAdjustReason] = useState('');
  // Diagnostic logs (Electron-side runtime logs)
  const [diagLogs, setDiagLogs] = useState([]);
  const [diagLevel, setDiagLevel] = useState('');
  const [diagCategory, setDiagCategory] = useState('');
  const [diagAutoRefresh, setDiagAutoRefresh] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [diagClearing, setDiagClearing] = useState(false);
  const [savingAdjust, setSavingAdjust] = useState(false);
  const [adjustments, setAdjustments] = useState([]);

  // Remote control / Console tab
  const [commands, setCommands] = useState([]);
  const [pendingCmd, setPendingCmd] = useState(null);
  const [showCmdConfirm, setShowCmdConfirm] = useState(null); // {cmd, label, args}

  // Print Preview tab
  const [previews, setPreviews] = useState([]);
  const [previewsLoading, setPreviewsLoading] = useState(false);
  const [zoomedPreview, setZoomedPreview] = useState(null);

  // Activity Log tab (React-side booth events — screen transitions, camera,
  // capture, payment, WS, errors). Full session reconstruction feed.
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityLevel, setActivityLevel] = useState('');
  const [activityCategory, setActivityCategory] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [activityAutoRefresh, setActivityAutoRefresh] = useState(true);
  const [activityExpandedId, setActivityExpandedId] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityOlder, setActivityOlder] = useState([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);

  // 3-step delete kiosk
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteAck, setDeleteAck] = useState(false);
  const [deleteNameInput, setDeleteNameInput] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const resetDeleteFlow = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeleteAck(false);
    setDeleteNameInput('');
    setDeleteConfirmText('');
  };

  const handleDeleteKiosk = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/kiosks/${kioskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Kiosk "${kiosk.name}" deleted`);
      resetDeleteFlow();
      navigate('/admin/kiosks');
    } catch (err) {
      console.error('Failed to delete kiosk:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete kiosk');
      setDeleting(false);
    }
  };

  const fetchData = async () => {
    try {
      const [kioskRes, templatesRes, pricingRes, inventoryRes, templateConfigRes, connectionRes] = await Promise.all([
        axios.get(`${API}/kiosks/${kioskId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kiosks/${kioskId}/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kiosks/${kioskId}/pricing`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory/${kioskId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kiosks/${kioskId}/template-config`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { backgroundColor: '#FFFFFF', customLogoUrl: null } })),
        axios.get(`${API}/kiosks/${kioskId}/connection-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setKiosk(kioskRes.data);
      setTemplates(templatesRes.data);
      setPricing(pricingRes.data);
      setInventory(inventoryRes.data);
      setTemplateConfig(templateConfigRes.data);
      setEditPricing(pricingRes.data);
      setEditUi(kioskRes.data.ui_config);
      setConnectionStatus(connectionRes.data);
    } catch (err) {
      console.error('Failed to fetch kiosk data:', err);
    }
    setLoading(false);
  };

  const fetchPrintLogs = async (page = printLogsPage, status = printLogsStatus) => {
    try {
      const params = { kiosk_id: kioskId, page, page_size: PRINT_LOGS_PAGE_SIZE };
      if (status) params.status = status;
      const response = await axios.get(`${API}/print-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setPrintLogs(response.data.logs || []);
      setPrintLogsTotal(response.data.total || 0);
      setPrintLogsHasMore(response.data.has_more || false);
      setPrintLogsPage(response.data.page || page);
    } catch (err) {
      console.error('Failed to fetch print logs:', err);
    }
  };

  const fetchAdjustments = async () => {
    try {
      const res = await axios.get(`${API}/inventory/${kioskId}/adjustments?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdjustments(res.data.adjustments || []);
    } catch {
      // ignore
    }
  };

  const fetchDiagLogs = async () => {
    try {
      const params = { limit: 300 };
      if (diagLevel) params.level = diagLevel;
      if (diagCategory) params.category = diagCategory;
      const res = await axios.get(`${API}/kiosks/${kioskId}/diagnostic-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDiagLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch diagnostic logs:', err);
    }
  };

  const clearDiagLogs = async () => {
    if (!window.confirm('Wipe all diagnostic logs for this booth? This cannot be undone.')) return;
    setDiagClearing(true);
    try {
      await axios.delete(`${API}/kiosks/${kioskId}/diagnostic-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiagLogs([]);
      toast.success('Diagnostic logs cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear logs');
    }
    setDiagClearing(false);
  };

  // ===== Remote control / Console =====
  const fetchCommands = async () => {
    try {
      const res = await axios.get(`${API}/kiosks/${kioskId}/commands`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 30 },
      });
      setCommands(res.data.commands || []);
    } catch (e) {
      console.error('Failed to fetch commands', e);
    }
  };

  const sendCommand = async (cmd, args) => {
    setPendingCmd(cmd);
    try {
      const res = await axios.post(
        `${API}/kiosks/${kioskId}/commands`,
        { cmd, args: args || {} },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Command queued (${res.data.delivered ? 'delivered' : 'pending — booth offline'})`);
      fetchCommands();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send command');
    }
    setPendingCmd(null);
    setShowCmdConfirm(null);
  };

  const submitAdjustment = async () => {
    setSavingAdjust(true);
    try {
      const res = await axios.post(`${API}/inventory/${kioskId}/adjust`,
        { type: adjustType, delta: parseInt(adjustDelta, 10) || 1, reason: adjustReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Inventory updated — ${res.data.remaining} prints remaining`);
      setShowAdjustModal(false);
      setAdjustDelta(1);
      setAdjustReason('');
      fetchData();
      fetchAdjustments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log adjustment');
    }
    setSavingAdjust(false);
  };

  // Poll connection status every 10 seconds
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/kiosks/${kioskId}/connection-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConnectionStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch connection status:', err);
      }
    };

    const interval = setInterval(pollStatus, 10000);
    return () => clearInterval(interval);
  }, [kioskId, token]);

  // Auto-refresh diagnostic logs every 4s while the Diagnostics tab is open
  useEffect(() => {
    if (activeTab !== 'diagnostics') return;
    fetchDiagLogs();
    if (!diagAutoRefresh) return;
    const interval = setInterval(fetchDiagLogs, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, diagAutoRefresh, diagLevel, diagCategory, kioskId]);

  // Auto-refresh remote commands every 3s while the Console tab is open
  useEffect(() => {
    if (activeTab !== 'console') return;
    fetchCommands();
    const interval = setInterval(fetchCommands, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, kioskId]);

  // Print Preview tab: load on entry + every 15s while open
  const fetchPreviews = async () => {
    setPreviewsLoading(true);
    try {
      const res = await axios.get(
        `${API}/kiosks/${kioskId}/print-previews?limit=24`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviews(res.data.previews || []);
    } catch (e) {
      // silent — log surface is enough
    }
    setPreviewsLoading(false);
  };
  useEffect(() => {
    if (activeTab !== 'print-preview') return;
    fetchPreviews();
    const interval = setInterval(fetchPreviews, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, kioskId]);

  // Activity Log tab — React-side booth events
  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300' });
      if (activityLevel) params.set('level', activityLevel);
      if (activityCategory) params.set('category', activityCategory);
      if (activitySearch) params.set('search', activitySearch);
      const res = await axios.get(
        `${API}/kiosks/${kioskId}/activity-log?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const rows = res.data.events || [];
      setActivityEvents(rows);
      setActivityHasMore(rows.length === 300);
    } catch (e) { /* silent */ }
    setActivityLoading(false);
  };

  // Infinite scroll — pull events OLDER than the oldest currently shown.
  // Kept in a separate list so the 4s auto-refresh (newest 300) doesn't wipe them.
  const loadOlderActivity = async () => {
    if (activityLoadingMore || !activityHasMore) return;
    const combined = [...activityEvents, ...activityOlder];
    const oldest = combined[combined.length - 1];
    if (!oldest?.server_ts) return;
    setActivityLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '200', before_ts: oldest.server_ts });
      if (activityLevel) params.set('level', activityLevel);
      if (activityCategory) params.set('category', activityCategory);
      if (activitySearch) params.set('search', activitySearch);
      const res = await axios.get(
        `${API}/kiosks/${kioskId}/activity-log?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const rows = res.data.events || [];
      setActivityOlder((prev) => [...prev, ...rows]);
      setActivityHasMore(rows.length === 200);
    } catch (e) { /* silent */ }
    setActivityLoadingMore(false);
  };

  const displayedActivity = React.useMemo(() => {
    const seen = new Set(activityEvents.map((e) => e.id));
    return [...activityEvents, ...activityOlder.filter((e) => !seen.has(e.id))];
  }, [activityEvents, activityOlder]);

  // Reset the older-events buffer whenever filters change.
  useEffect(() => {
    setActivityOlder([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityLevel, activityCategory, activitySearch, kioskId]);

  // Download the FULL activity log for this booth as a JSON file.
  // Fetches up to 2000 most-recent events (no filters — we want the raw
  // stream so an engineer can grep locally). One-click share for
  // debugging: user clicks, browser saves the file, paste to a
  // support channel.
  const downloadActivity = async () => {
    try {
      const params = new URLSearchParams({ limit: '2000' });
      const res = await axios.get(
        `${API}/kiosks/${kioskId}/activity-log?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const events = res.data.events || [];
      const payload = {
        kiosk_id: kioskId,
        kiosk_name: kiosk?.name,
        exported_at: new Date().toISOString(),
        event_count: events.length,
        events,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `piczoo-activity-${kiosk?.name || kioskId}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${events.length} events`);
    } catch (e) {
      toast.error('Download failed — try again');
    }
  };
  useEffect(() => {
    if (activeTab !== 'activity') return;
    fetchActivity();
    if (!activityAutoRefresh) return;
    const interval = setInterval(fetchActivity, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activityAutoRefresh, activityLevel, activityCategory, activitySearch, kioskId]);

  const deletePreview = async (previewId) => {
    if (!window.confirm('Delete this preview from history?')) return;
    try {
      await axios.delete(
        `${API}/kiosks/${kioskId}/print-previews/${previewId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviews((p) => p.filter((x) => x.id !== previewId));
      toast.success('Preview deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  useEffect(() => {
    fetchData();
    fetchPrintLogs();
    fetchAdjustments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskId, token]);

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kiosks/${kioskId}/pricing`, {
        one_unit_price: editPricing.one_unit_price,
        two_unit_price: editPricing.two_unit_price,
        three_unit_price: editPricing.three_unit_price,
        enabled_quantities: editPricing.enabled_quantities?.length ? editPricing.enabled_quantities : [1, 2, 3],
        schedules: editPricing.schedules || []
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPricing(editPricing);
      toast.success('Pricing updated');
    } catch (err) {
      console.error('Failed to update pricing:', err);
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    }
    setSaving(false);
  };

  const handleSaveUi = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kiosks/${kioskId}`, {
        ui_config: editUi
      }, { headers: { Authorization: `Bearer ${token}` } });
      setKiosk({ ...kiosk, ui_config: editUi });
      toast.success('Appearance saved');
    } catch (err) {
      console.error('Failed to update UI config:', err);
      toast.error(err.response?.data?.detail || 'Failed to save appearance');
    }
    setSaving(false);
  };

  const handleResetInventory = async (target = 'paper') => {
    const msg = target === 'ribbon'
      ? 'Reset ribbon counter? This should only be done after changing the ink ribbon.'
      : 'Reset paper counter? This should only be done after refilling paper.';
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/inventory/${kioskId}/reset?target=${target}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success(target === 'ribbon' ? 'Ribbon counter reset' : 'Paper counter reset');
    } catch (err) {
      console.error('Failed to reset inventory:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset inventory');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const handleRegeneratePairingCode = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/kiosks/${kioskId}/regenerate-pairing-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKiosk({ ...kiosk, pairing_code: response.data.pairing_code, status: 'offline' });
      setShowRegenerateModal(false);
      toast.success('New pairing code generated');
    } catch (err) {
      console.error('Failed to regenerate pairing code:', err);
      toast.error(err.response?.data?.detail || 'Failed to regenerate pairing code');
    }
    setRegenerating(false);
  };

  const handleForceDisconnect = async () => {
    setDisconnecting(true);
    try {
      const response = await axios.post(`${API}/kiosks/${kioskId}/force-disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKiosk({ ...kiosk, pairing_code: response.data.new_pairing_code, status: 'offline' });
      setConnectionStatus({ ...connectionStatus, connected_realtime: false });
      setShowDisconnectModal(false);
      toast.success('Kiosk disconnected');
    } catch (err) {
      console.error('Failed to force disconnect:', err);
      toast.error(err.response?.data?.detail || 'Failed to disconnect kiosk');
    }
    setDisconnecting(false);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.put(`${API}/kiosks/${kioskId}/admin-password`, 
        { admin_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKiosk({ ...kiosk, admin_password: newPassword });
      setNewPassword('');
      setShowPasswordModal(false);
      toast.success('Admin password updated');
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
    setPasswordSaving(false);
  };

  const copyPairingCode = async () => {
    try {
      await navigator.clipboard.writeText(kiosk.pairing_code);
    } catch (err) {
      // Fallback for environments where clipboard API is blocked
      const textArea = document.createElement('textarea');
      textArea.value = kiosk.pairing_code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopySuccess(true);
    toast.success('Pairing code copied');
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 w-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!kiosk) {
    return (
      <div className="p-8 text-center">
        <h2 className="font-serif text-2xl text-charcoal-900">Kiosk not found</h2>
      </div>
    );
  }

  const tabs = [
    { id: 'template', label: 'Print Template', icon: <Grid className="w-4 h-4" /> },
    { id: 'pricing', label: 'Pricing', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'inventory', label: 'Inventory', icon: <Package className="w-4 h-4" /> },
    { id: 'print-history', label: 'Print History', icon: <Printer className="w-4 h-4" /> },
    { id: 'print-preview', label: 'Print Preview', icon: <Image className="w-4 h-4" /> },
    { id: 'activity', label: 'Activity Log', icon: <ListVideo className="w-4 h-4" /> },
    { id: 'diagnostics', label: 'Diagnostics', icon: <Activity className="w-4 h-4" /> },
    { id: 'console', label: 'Console', icon: <Power className="w-4 h-4" /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> }
  ].filter((t) => editable || !['console', 'appearance', 'settings'].includes(t.id));

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="kiosk-detail-page">
      {/* Header */}
      <button
        onClick={() => navigate('/admin/kiosks')}
        className="flex items-center gap-2 text-charcoal-800/60 hover:text-charcoal-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Booths
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Monitor className="w-6 h-6 sm:w-8 sm:h-8 text-charcoal-900" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900 break-words">{kiosk.name}</h1>
            <div className="flex items-center gap-3 sm:gap-4 mt-2 flex-wrap">
              <div className="flex items-center gap-1 text-charcoal-800/60 text-sm">
                <MapPin className="w-4 h-4" />
                {kiosk.location}
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                kiosk.status === 'online' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {kiosk.status === 'online' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {kiosk.status}
              </div>
              {kiosk.camera_mode && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    kiosk.camera_mode === 'native'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-50 text-blue-600'
                  }`}
                  title={kiosk.camera_mode === 'native'
                    ? 'This booth is using the ffmpeg Native Camera Helper (WebRTC bypassed — usually after a camera crash failover)'
                    : 'This booth is using the standard Chromium camera'}
                  data-testid="camera-mode-badge"
                >
                  <Camera className="w-3 h-3" />
                  {kiosk.camera_mode === 'native' ? 'Native Camera' : 'Standard Camera'}
                </div>
              )}
              {kiosk.app_version && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 font-mono" data-testid="booth-version-badge">
                  v{kiosk.app_version}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pairing Code Banner */}
      <div className="bg-charcoal-900 text-white rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-white/60 text-xs sm:text-sm mb-1">Pairing Code</p>
          <p className="font-mono text-2xl sm:text-3xl tracking-wider" data-testid="pairing-code-display">{kiosk.pairing_code}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={copyPairingCode}
            className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
            data-testid="copy-pairing-code-btn"
          >
            {copySuccess ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copySuccess ? 'Copied!' : 'Copy'}
          </button>
          {editable && (
          <button
            onClick={() => setShowRegenerateModal(true)}
            disabled={regenerating}
            className="px-4 py-2 bg-rose-500/20 text-rose-200 rounded-lg hover:bg-rose-500/30 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="regenerate-code-btn"
          >
            <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regenerating...' : 'Regenerate'}
          </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-charcoal-900/10 pb-4 overflow-x-auto -mx-2 px-2 scrollbar-thin">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === tab.id
                ? 'bg-charcoal-900 text-white'
                : 'text-charcoal-800/60 hover:bg-cream-100'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'template' && (
        <div>
          <h2 className="font-serif text-xl text-charcoal-900 mb-6">Print Template</h2>
          <SimpleTemplateEditor 
            kioskId={kioskId}
            initialConfig={templateConfig}
            onSave={(config) => setTemplateConfig(config)}
          />
        </div>
      )}

      {activeTab === 'pricing' && editPricing && (
        <div className="max-w-md">
          <h2 className="font-serif text-xl text-charcoal-900 mb-6">Print Pricing</h2>
          <div className="card-dashboard space-y-4">
            {[
              { q: 1, key: 'one_unit_price', label: '1 Unit Price (₹)', hint: '2x6" = 2 copies, 4x6" = 1 copy' },
              { q: 2, key: 'two_unit_price', label: '2 Units Price (₹)', hint: null },
              { q: 3, key: 'three_unit_price', label: '3 Units Price (₹)', hint: null },
            ].map(({ q, key, label, hint }) => {
              const enabledQ = editPricing.enabled_quantities?.length ? editPricing.enabled_quantities : [1, 2, 3];
              const isOn = enabledQ.includes(q);
              return (
                <div key={q} className={isOn ? '' : 'opacity-50'}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-charcoal-800">{label}</label>
                    <label className="flex items-center gap-2 text-xs text-charcoal-800/60 cursor-pointer select-none" title="Untick to hide this option on the booth screen">
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => {
                          const next = isOn ? enabledQ.filter((x) => x !== q) : [...enabledQ, q].sort();
                          if (next.length === 0) { toast.error('At least one print option must stay enabled'); return; }
                          setEditPricing({ ...editPricing, enabled_quantities: next });
                        }}
                        className="w-4 h-4 accent-charcoal-900"
                        data-testid={`offer-quantity-${q}-checkbox`}
                      />
                      Offered on booth
                    </label>
                  </div>
                  <input
                    type="number"
                    value={editPricing[key]}
                    onChange={(e) => setEditPricing({ ...editPricing, [key]: parseInt(e.target.value) })}
                    disabled={!isOn}
                    className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl disabled:cursor-not-allowed"
                    data-testid={`price-${q}-input`}
                  />
                  {hint && <p className="text-xs text-charcoal-800/40 mt-1">{hint}</p>}
                </div>
              );
            })}
            {/* Busy Hour Schedules */}
            <div className="pt-4 border-t border-charcoal-900/10">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-charcoal-900">Busy Hour Schedules</h3>
                {editable && (
                  <button
                    onClick={() => setEditPricing({
                      ...editPricing,
                      schedules: [...(editPricing.schedules || []), { label: '', start: '18:00', end: '22:00' }]
                    })}
                    className="text-xs px-3 py-1.5 border border-charcoal-900/10 rounded-lg hover:bg-cream-50"
                    data-testid="add-schedule-btn"
                  >
                    + Add schedule
                  </button>
                )}
              </div>
              <p className="text-xs text-charcoal-800/40 mb-3">
                Override prices/options during a time window (booth local time). First matching schedule wins. Overnight ranges like 22:00–02:00 work. Blank price = use base price.
              </p>
              {(editPricing.schedules || []).map((s, i) => {
                const upd = (patch) => {
                  const next = [...editPricing.schedules];
                  next[i] = { ...next[i], ...patch };
                  setEditPricing({ ...editPricing, schedules: next });
                };
                const schedEQ = s.enabled_quantities?.length ? s.enabled_quantities : [1, 2, 3];
                return (
                  <div key={i} className="bg-cream-50 border border-charcoal-900/10 rounded-xl p-3 mb-3 space-y-2" data-testid={`schedule-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="text" placeholder="Label (e.g. Evening peak)" value={s.label || ''}
                        onChange={(e) => upd({ label: e.target.value })}
                        className="flex-1 px-3 py-2 bg-white border border-charcoal-900/10 rounded-lg text-sm"
                        data-testid={`schedule-${i}-label`}
                      />
                      <input type="time" value={s.start || ''} onChange={(e) => upd({ start: e.target.value })}
                        className="px-2 py-2 bg-white border border-charcoal-900/10 rounded-lg text-sm" data-testid={`schedule-${i}-start`} />
                      <span className="text-xs text-charcoal-800/40">to</span>
                      <input type="time" value={s.end || ''} onChange={(e) => upd({ end: e.target.value })}
                        className="px-2 py-2 bg-white border border-charcoal-900/10 rounded-lg text-sm" data-testid={`schedule-${i}-end`} />
                      {editable && (
                        <button
                          onClick={() => setEditPricing({ ...editPricing, schedules: editPricing.schedules.filter((_, x) => x !== i) })}
                          className="text-red-500 hover:text-red-700 text-lg leading-none px-1" title="Remove schedule"
                          data-testid={`schedule-${i}-remove`}
                        >×</button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d, di) => {
                        const on = !s.days?.length || s.days.includes(di);
                        return (
                          <button
                            key={di}
                            onClick={() => {
                              const cur = s.days?.length ? s.days : [0, 1, 2, 3, 4, 5, 6];
                              const next = cur.includes(di) ? cur.filter((x) => x !== di) : [...cur, di].sort();
                              if (next.length === 0) { toast.error('Keep at least one day'); return; }
                              upd({ days: next.length === 7 ? undefined : next });
                            }}
                            className={`w-8 h-7 rounded text-[11px] font-medium ${on ? 'bg-charcoal-900 text-white' : 'bg-white text-charcoal-800/40 border border-charcoal-900/10'}`}
                            data-testid={`schedule-${i}-day-${di}`}
                          >{d}</button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      {[['one_unit_price', 1], ['two_unit_price', 2], ['three_unit_price', 3]].map(([key, q]) => (
                        <div key={key} className="flex items-center gap-1">
                          <label className="flex items-center gap-1 text-[11px] text-charcoal-800/60">
                            <input
                              type="checkbox" checked={schedEQ.includes(q)}
                              onChange={() => {
                                const next = schedEQ.includes(q) ? schedEQ.filter((x) => x !== q) : [...schedEQ, q].sort();
                                if (next.length === 0) { toast.error('Keep at least one option'); return; }
                                upd({ enabled_quantities: next });
                              }}
                              className="w-3.5 h-3.5 accent-charcoal-900"
                              data-testid={`schedule-${i}-offer-${q}`}
                            />
                            {q}×
                          </label>
                          <input
                            type="number" placeholder={`₹${editPricing[key] || ''}`}
                            value={s[key] ?? ''}
                            onChange={(e) => upd({ [key]: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                            disabled={!schedEQ.includes(q)}
                            className="w-20 px-2 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm disabled:opacity-40"
                            data-testid={`schedule-${i}-price-${q}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {!(editPricing.schedules || []).length && (
                <p className="text-xs text-charcoal-800/30 italic">No schedules — base pricing applies all day.</p>
              )}
            </div>
            {editable && (
            <button
              onClick={handleSavePricing}
              disabled={saving}
              className="btn-primary w-full mt-4 disabled:opacity-50"
              data-testid="save-pricing-btn"
            >
              {saving ? 'Saving...' : 'Save Pricing'}
            </button>
            )}
          </div>
        </div>
      )}

      {activeTab === 'inventory' && inventory && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* Printer hardware counter — the single source of truth (manual counting removed) */}
          <div className="card-dashboard" data-testid="hardware-media-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-charcoal-900 flex items-center gap-2">
                <Printer className="w-5 h-5" /> Printer Counter
              </h2>
              {editable && (
              <button
                onClick={async () => { await sendCommand('read_media_counter'); setTimeout(fetchData, 8000); }}
                disabled={pendingCmd === 'read_media_counter'}
                className="text-xs px-3 py-1.5 border border-charcoal-900/10 rounded-lg hover:bg-cream-50 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="read-media-counter-btn"
              >
                <RefreshCw className="w-3 h-3" /> Read counter now
              </button>
              )}
            </div>
            {inventory.hardware?.supported && inventory.hardware.remaining != null ? (
              (() => {
                const est = inventory.hardware.estimate ?? inventory.hardware.remaining;
                const cap = inventory.hardware.initial || 700;
                return (
                  <>
                    <div className="flex items-end justify-between mb-3">
                      <span className="text-charcoal-800/60">Prints remaining</span>
                      <span className="font-mono text-4xl leading-none" data-testid="hardware-remaining">
                        <span className={
                          est <= 50 ? 'text-red-600' : est <= 100 ? 'text-amber-600' : 'text-green-600'
                        }>{est}</span>
                        {inventory.hardware.initial ? <span className="text-base text-charcoal-800/40"> / {cap}</span> : null}
                      </span>
                    </div>
                    <div className="h-4 bg-cream-100 rounded-full overflow-hidden mb-4">
                      <div
                        className={`h-full rounded-full transition-all ${
                          est <= 50 ? 'bg-red-500' : est <= 100 ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (est / cap) * 100)}%` }}
                      />
                    </div>
                    {est <= 100 && (
                      <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
                        est <= 50 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{est <= 50 ? 'Critical: load a new media kit soon!' : 'Running low — plan a refill'}</span>
                      </div>
                    )}
                    <p className="text-xs text-charcoal-800/40">
                      Read from the printer's ribbon RFID counter
                      {inventory.hardware.updated_at && ` · last hardware read ${istDateTime(inventory.hardware.updated_at)}`}
                      {inventory.hardware.estimate != null && inventory.hardware.estimate !== inventory.hardware.remaining &&
                        ` · live estimate (${inventory.hardware.remaining} at read − prints since)`}
                    </p>
                    <p className="text-xs text-charcoal-800/50 mt-3 bg-cream-50 rounded-lg p-3">
                      Firmware-accurate, resets automatically when you load a new roll — nothing to update manually, ever.
                    </p>
                  </>
                );
              })()
            ) : (
              <div className="text-sm text-charcoal-800/60 space-y-3" data-testid="media-setup-needed">
                <p className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Setup needed — this booth isn't reporting its printer counter yet.
                </p>
                {inventory.hardware?.error && (
                  <p className="text-xs font-mono text-charcoal-800/50 bg-cream-50 rounded p-2 break-all" data-testid="hardware-media-error">
                    {inventory.hardware.error}
                  </p>
                )}
                <ol className="text-xs text-charcoal-800/50 space-y-1 list-decimal list-inside">
                  <li>Install the DNP PrinterInfo tool on the booth PC — get it from the <a href="/admin/software" className="underline">Software page</a></li>
                  <li>Make sure the booth runs kiosk app v3.14.3 or newer</li>
                  <li>Hit "Read counter now" above to verify</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'print-history' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="font-serif text-xl text-charcoal-900">Print History</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={printLogsStatus}
                onChange={(e) => { setPrintLogsStatus(e.target.value); fetchPrintLogs(1, e.target.value); }}
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm focus:outline-none focus:border-charcoal-900/30"
                data-testid="print-logs-status-filter"
              >
                <option value="">All statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
              <span className="text-sm text-charcoal-800/60">{printLogsTotal} total</span>
            </div>
          </div>
          {printLogs.length === 0 ? (
            <div className="card-dashboard text-center py-12">
              <Printer className="w-10 h-10 mx-auto text-charcoal-800/20 mb-3" />
              <p className="text-charcoal-800/40 text-sm">No print logs yet</p>
            </div>
          ) : (
            <>
              <div className="card-dashboard overflow-x-auto">
                <table className="w-full" data-testid="print-logs-table">
                  <thead>
                    <tr className="border-b border-charcoal-900/10 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider">Session</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {printLogs.map((log, i) => (
                      <tr key={i} className="hover:bg-cream-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-charcoal-800/70">
                          {istDateTime(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-charcoal-800/50">
                          {log.session_id?.slice(0, 8) || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            log.status === 'success' ? 'bg-green-50 text-green-700' :
                            log.status === 'failed' ? 'bg-red-50 text-red-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                            {log.status || 'printed'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-charcoal-800/70">
                          {log.quantity || 1}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={() => fetchPrintLogs(Math.max(1, printLogsPage - 1), printLogsStatus)}
                  disabled={printLogsPage <= 1}
                  className="px-3 py-1.5 text-sm border border-charcoal-900/10 rounded-lg hover:bg-cream-50 disabled:opacity-40"
                  data-testid="print-logs-prev"
                >
                  ← Prev
                </button>
                <span className="text-sm text-charcoal-800/60">Page {printLogsPage}</span>
                <button
                  onClick={() => fetchPrintLogs(printLogsPage + 1, printLogsStatus)}
                  disabled={!printLogsHasMore}
                  className="px-3 py-1.5 text-sm border border-charcoal-900/10 rounded-lg hover:bg-cream-50 disabled:opacity-40"
                  data-testid="print-logs-next"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'print-preview' && (
        <div data-testid="print-preview-tab">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h2 className="font-serif text-xl text-charcoal-900">Print Preview</h2>
              <p className="text-sm text-charcoal-800/60">
                Thumbnails of every successful print on this booth, uploaded automatically.
                Verify template changes end-to-end without going on-site. Auto-refreshes every 15 s; auto-deleted after 7 days.
              </p>
            </div>
            <button
              onClick={fetchPreviews}
              disabled={previewsLoading}
              data-testid="print-preview-refresh"
              className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-charcoal-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${previewsLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {previewsLoading && previews.length === 0 ? (
            <div className="text-center py-16 text-charcoal-800/40">Loading previews…</div>
          ) : previews.length === 0 ? (
            <div className="card-dashboard text-center py-16">
              <Image className="w-12 h-12 text-charcoal-800/20 mx-auto mb-3" />
              <div className="text-charcoal-800/60 font-medium">No prints yet on this booth</div>
              <div className="text-xs text-charcoal-800/40 mt-1">
                Previews appear here within ~10 seconds of every successful print. Booth must be running v3.6.0+ of the kiosk software.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="print-preview-grid">
              {previews.map((p) => (
                <div
                  key={p.id}
                  className="card-dashboard p-3 flex flex-col"
                  data-testid={`print-preview-${p.id}`}
                >
                  <button
                    onClick={() => setZoomedPreview(p)}
                    className="aspect-[3/4] bg-charcoal-900/5 rounded-lg overflow-hidden mb-3 flex items-center justify-center group relative"
                    data-testid={`print-preview-img-${p.id}`}
                  >
                    {p.image_b64 ? (
                      <img
                        src={`data:${p.mime || 'image/jpeg'};base64,${p.image_b64}`}
                        alt={`Print at ${p.created_at}`}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-charcoal-800/30" />
                    )}
                  </button>
                  <div className="flex items-center justify-between text-xs text-charcoal-800/60 mb-1">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {istDateTime(p.created_at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {p.duration_ms ? (
                      <span title="Helper duration (excludes physical print time)">
                        {(p.duration_ms / 1000).toFixed(1)}s
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-charcoal-800/50">
                    <span className={`px-1.5 py-0.5 rounded ${
                      p.backend === 'exe' ? 'bg-green-100 text-green-800'
                        : p.backend === 'powershell' ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`} title="Print backend used">
                      {p.backend || 'mspaint'}
                    </span>
                    {editable && (
                    <button
                      onClick={() => deletePreview(p.id)}
                      data-testid={`print-preview-delete-${p.id}`}
                      className="text-charcoal-800/40 hover:text-red-600"
                      title="Delete this preview"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {zoomedPreview && (
            <div
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
              onClick={() => setZoomedPreview(null)}
              data-testid="print-preview-zoom"
            >
              <div className="bg-cream-50 rounded-lg p-4 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm">
                    <div className="font-medium text-charcoal-900">{istDateTime(zoomedPreview.created_at)}</div>
                    <div className="text-xs text-charcoal-800/60">
                      {zoomedPreview.printer_name} · backend: {zoomedPreview.backend} · rotation: {zoomedPreview.rotation}°
                      {zoomedPreview.duration_ms ? ` · ${(zoomedPreview.duration_ms / 1000).toFixed(1)}s` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => setZoomedPreview(null)}
                    className="text-charcoal-800/60 hover:text-charcoal-900"
                    data-testid="print-preview-zoom-close"
                  >
                    ✕
                  </button>
                </div>
                <div className="bg-white rounded flex items-center justify-center max-h-[70vh] overflow-auto">
                  <img
                    src={`data:${zoomedPreview.mime || 'image/jpeg'};base64,${zoomedPreview.image_b64}`}
                    alt="Full print preview"
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div data-testid="activity-log-tab">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
            <div>
              <h2 className="font-serif text-xl text-charcoal-900">Booth Activity Log</h2>
              <p className="text-sm text-charcoal-800/60 max-w-xl">
                Every screen transition, camera event, capture step, payment event, and WebSocket message from the React app.
                Use this to reconstruct any session end-to-end — including the exact sequence leading up to a crash.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={activityLevel}
                onChange={(e) => setActivityLevel(e.target.value)}
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm"
                data-testid="activity-level-filter"
              >
                <option value="">All levels</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warn">Warnings</option>
                <option value="error">Errors</option>
              </select>
              <select
                value={activityCategory}
                onChange={(e) => setActivityCategory(e.target.value)}
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm"
                data-testid="activity-category-filter"
              >
                <option value="">All categories</option>
                <option value="screen">Screen transitions</option>
                <option value="pair">Pairing</option>
                <option value="ws">WebSocket</option>
                <option value="camera">Camera</option>
                <option value="capture">Photo capture</option>
                <option value="payment">Payment</option>
                <option value="error">Errors</option>
              </select>
              <input
                type="text"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                placeholder="Search messages…"
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm w-44"
                data-testid="activity-search-input"
              />
              <label className="flex items-center gap-2 text-sm text-charcoal-800/70 px-3 py-1.5 border border-charcoal-900/10 rounded-lg bg-white">
                <input
                  type="checkbox"
                  checked={activityAutoRefresh}
                  onChange={(e) => setActivityAutoRefresh(e.target.checked)}
                  data-testid="activity-auto-refresh"
                />
                Auto-refresh (4s)
              </label>
              <button
                onClick={fetchActivity}
                className="px-3 py-1.5 text-sm border border-charcoal-900/10 rounded-lg hover:bg-cream-50 flex items-center gap-1.5"
                data-testid="activity-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
              <button
                onClick={downloadActivity}
                className="px-3 py-1.5 text-sm border border-charcoal-900/10 rounded-lg hover:bg-cream-50 flex items-center gap-1.5"
                data-testid="activity-download-btn"
                title="Download last 2000 events as JSON for sharing"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-charcoal-900/10 overflow-hidden">
            <div className="px-4 py-2 border-b border-charcoal-900/5 text-xs text-charcoal-800/60 flex items-center justify-between">
              <span>{activityLoading ? 'Loading…' : `${displayedActivity.length} event${displayedActivity.length === 1 ? '' : 's'}`}</span>
              <span className="font-mono">newest first</span>
            </div>
            {displayedActivity.length === 0 && !activityLoading ? (
              <div className="p-8 text-center text-sm text-charcoal-800/40">
                No events match your filters. Try clearing the filters or wait for the booth to send more activity.
              </div>
            ) : (
              <ul className="divide-y divide-charcoal-900/5 max-h-[70vh] overflow-y-auto">
                {displayedActivity.map(ev => {
                  const levelColor =
                    ev.level === 'error' ? 'text-red-700 bg-red-50' :
                    ev.level === 'warn' ? 'text-amber-700 bg-amber-50' :
                    ev.level === 'debug' ? 'text-charcoal-800/50 bg-cream-50' :
                    'text-blue-700 bg-blue-50';
                  return (
                    <li
                      key={ev.id}
                      className="px-4 py-2.5 text-sm hover:bg-cream-50 cursor-pointer"
                      onClick={() => setActivityExpandedId(activityExpandedId === ev.id ? null : ev.id)}
                      data-testid={`activity-row-${ev.id}`}
                    >
                      <div className="flex items-start gap-3 flex-wrap">
                        <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${levelColor}`}>
                          {ev.level}
                        </span>
                        <span className="text-[10px] font-mono text-charcoal-800/50">
                          {istTime(ev.server_ts, { hour12: false })}
                        </span>
                        {ev.category && (
                          <span className="text-[10px] uppercase tracking-wider text-charcoal-800/60 bg-cream-100 px-1.5 py-0.5 rounded">
                            {ev.category}/{ev.event}
                          </span>
                        )}
                        {ev.screen && (
                          <span className="text-[10px] uppercase tracking-wider text-charcoal-800/40">
                            @{ev.screen}
                          </span>
                        )}
                        <span className="flex-1 min-w-0 text-charcoal-900 truncate">{ev.message || '—'}</span>
                      </div>
                      {activityExpandedId === ev.id && (
                        <div className="mt-2 ml-6 space-y-1">
                          {ev.session_id && (
                            <div className="text-[10px] text-charcoal-800/50 font-mono">
                              session: {ev.session_id}
                            </div>
                          )}
                          {ev.data && (
                            <pre className="bg-charcoal-900 text-cream-100 text-[10px] p-2 rounded overflow-auto max-h-64 whitespace-pre-wrap break-all">
{(() => { try { return JSON.stringify(JSON.parse(ev.data), null, 2); } catch { return ev.data; } })()}
                            </pre>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
                <li className="list-none">
                  <ScrollSentinel
                    onVisible={loadOlderActivity}
                    disabled={activityLoading || activityLoadingMore || !activityHasMore}
                    testid="activity-scroll-sentinel"
                  />
                  {activityLoadingMore && (
                    <div className="text-center py-2 text-xs text-charcoal-800/40">Loading older events…</div>
                  )}
                </li>
              </ul>
            )}
          </div>
        </div>
      )}



      {activeTab === 'diagnostics' && (
        <div data-testid="diagnostics-tab">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h2 className="font-serif text-xl text-charcoal-900">Booth Diagnostics</h2>
              <p className="text-sm text-charcoal-800/60">Live runtime logs streamed from the Electron booth software (printer status, print attempts, mspaint exit codes, WMI snapshots).</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={diagLevel}
                onChange={(e) => setDiagLevel(e.target.value)}
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm focus:outline-none focus:border-charcoal-900/30"
                data-testid="diag-level-filter"
              >
                <option value="">All levels</option>
                <option value="info">Info</option>
                <option value="warn">Warnings</option>
                <option value="error">Errors</option>
              </select>
              <select
                value={diagCategory}
                onChange={(e) => setDiagCategory(e.target.value)}
                className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm focus:outline-none focus:border-charcoal-900/30"
                data-testid="diag-category-filter"
              >
                <option value="">All categories</option>
                <option value="lifecycle">Lifecycle</option>
                <option value="printer-status">Printer Status (WMI)</option>
                <option value="printer-list">Printer List</option>
                <option value="print">Print Pipeline</option>
                <option value="print-mspaint">mspaint /pt</option>
                <option value="console">Console</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-charcoal-800/70 px-3 py-1.5 border border-charcoal-900/10 rounded-lg bg-white">
                <input
                  type="checkbox"
                  checked={diagAutoRefresh}
                  onChange={(e) => setDiagAutoRefresh(e.target.checked)}
                  data-testid="diag-auto-refresh"
                />
                Auto-refresh (4s)
              </label>
              <button
                onClick={fetchDiagLogs}
                className="px-3 py-1.5 text-sm border border-charcoal-900/10 rounded-lg hover:bg-cream-50 flex items-center gap-1.5"
                data-testid="diag-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
              {editable && (
              <button
                onClick={clearDiagLogs}
                disabled={diagClearing}
                className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-40 flex items-center gap-1.5"
                data-testid="diag-clear-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
              )}
            </div>
          </div>

          {diagLogs.length === 0 ? (
            <div className="card-dashboard text-center py-12">
              <Activity className="w-10 h-10 mx-auto text-charcoal-800/20 mb-3" />
              <p className="text-charcoal-800/60 text-sm font-medium mb-1">No diagnostic logs yet</p>
              <p className="text-charcoal-800/40 text-xs max-w-md mx-auto">
                Logs appear here automatically once the booth software runs (it ships them to the backend every 5 seconds).
                Make sure the booth is running v3.1.0+ and is paired.
              </p>
            </div>
          ) : (
            <div className="card-dashboard p-0 overflow-hidden">
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm" data-testid="diag-logs-table">
                  <thead className="bg-cream-50 sticky top-0 z-10">
                    <tr className="border-b border-charcoal-900/10 text-left">
                      <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider w-44">Time</th>
                      <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider w-20">Level</th>
                      <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider w-36">Category</th>
                      <th className="px-3 py-2 text-xs font-medium text-charcoal-800/60 uppercase tracking-wider">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {diagLogs.map((log) => {
                      const isExpanded = expandedLogId === log.id;
                      const hasData = log.data !== null && log.data !== undefined;
                      const levelColor = {
                        info: 'bg-blue-50 text-blue-700',
                        warn: 'bg-amber-50 text-amber-800',
                        error: 'bg-red-50 text-red-700',
                      }[log.level] || 'bg-gray-50 text-gray-700';
                      return (
                        <React.Fragment key={log.id}>
                          <tr
                            className={`hover:bg-cream-50 transition-colors ${hasData ? 'cursor-pointer' : ''}`}
                            onClick={() => hasData && setExpandedLogId(isExpanded ? null : log.id)}
                          >
                            <td className="px-3 py-2 text-xs text-charcoal-800/60 font-mono whitespace-nowrap">
                              {log.client_ts ? istTime(log.client_ts) : istTime(log.server_ts)}
                              <div className="text-[10px] text-charcoal-800/40">
                                {log.client_ts ? istDate(log.client_ts) : istDate(log.server_ts)}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${levelColor}`}>
                                {log.level}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs font-mono text-charcoal-800/70">
                              {log.category}
                            </td>
                            <td className="px-3 py-2 text-charcoal-900 break-words">
                              <div className="flex items-start gap-2">
                                <span className="flex-1">{log.message}</span>
                                {hasData && (
                                  <span className="text-[10px] text-charcoal-800/40 px-1.5 py-0.5 rounded bg-cream-100 whitespace-nowrap">
                                    {isExpanded ? 'Hide' : '+ data'}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && hasData && (
                            <tr className="bg-charcoal-900 text-cream-50">
                              <td colSpan={4} className="px-3 py-3">
                                <pre className="text-[11px] font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                                  {JSON.stringify(log.data, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-charcoal-900/10 bg-cream-50 text-xs text-charcoal-800/60 flex items-center justify-between">
                <span>{diagLogs.length} entries shown · most recent at top</span>
                {diagAutoRefresh && (
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}


      {activeTab === 'console' && (
        <div data-testid="console-tab" className="space-y-5">
          <div>
            <h2 className="font-serif text-xl text-charcoal-900">Booth Console</h2>
            <p className="text-sm text-charcoal-800/60">Send commands directly to the booth software. All actions are audited.</p>
          </div>

          {/* Quick action grid */}
          <div className="card-dashboard">
            <h3 className="text-sm font-medium text-charcoal-800/80 mb-3">Quick actions</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              <ConsoleButton testid="cmd-reboot" onClick={() => setShowCmdConfirm({ cmd: 'reboot', label: 'Reboot booth', danger: true, body: 'The booth will close and restart in ~5 seconds. Any active customer session will be lost.' })} icon="🔄" label="Reboot" />
              <ConsoleButton testid="cmd-print-test" onClick={() => setShowCmdConfirm({ cmd: 'print_test', label: 'Print test page', body: 'A calibration strip will print on this booth\'s configured printer.' })} icon="🖨️" label="Print test" />
              <ConsoleButton testid="cmd-screenshot" onClick={() => setShowCmdConfirm({ cmd: 'screenshot', label: 'Capture screenshot', body: 'Captures what is currently on the booth screen.' })} icon="📸" label="Screenshot" />
              <ConsoleButton testid="cmd-system-info" onClick={() => sendCommand('get_system_info')} icon="⚙️" label="Sys info" />
              <ConsoleButton testid="cmd-force-setup" onClick={() => setShowCmdConfirm({ cmd: 'force_setup', label: 'Force setup wizard', danger: true, body: 'The booth will exit kiosk mode and re-run the 3-step setup wizard. The operator will need to re-pair.' })} icon="🔧" label="Force setup" />
              <ConsoleButton testid="cmd-clear-cache" onClick={() => setShowCmdConfirm({ cmd: 'clear_cache', label: 'Clear cache', body: 'Clears local diag buffer and temp files on the booth.' })} icon="🧹" label="Clear cache" />
              <ConsoleButton testid="cmd-update-check" onClick={() => sendCommand('update_check')} icon="🔁" label="Update check" />
              <ConsoleButton
                testid="cmd-update-install-now"
                onClick={() => setShowCmdConfirm({
                  cmd: 'update_install_now',
                  label: 'Force update now',
                  danger: true,
                  body: 'Downloads the latest published kiosk version and restarts the booth to apply it. The booth will be unavailable for ~15 seconds. Any active customer session will be lost. Use only when the booth is idle.',
                })}
                icon="⬇️"
                label="Force update"
                superOnly
              />
              <ConsoleButton testid="cmd-lock" onClick={() => setShowCmdConfirm({ cmd: 'lock', label: 'Lock booth', danger: true, body: 'Shows a full-screen lock overlay. Use only if booth is unattended or compromised. Super admin only.' })} icon="🔒" label="Lock" superOnly />
              <ConsoleButton testid="cmd-unlock" onClick={() => setShowCmdConfirm({ cmd: 'unlock', label: 'Unlock booth', body: 'Removes the lock overlay.' })} icon="🔓" label="Unlock" superOnly />
            </div>
          </div>

          {/* Windows autostart fleet toggle */}
          <AutostartToggleCard
            kiosk={kiosk}
            kioskId={kioskId}
            token={token}
            onApplied={fetchData}
          />

          {/* System info snapshot */}
          {kiosk?.system_info && (
            <div className="card-dashboard">
              <h3 className="text-sm font-medium text-charcoal-800/80 mb-2">Last system snapshot</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {Object.entries(kiosk.system_info).map(([k, v]) => (
                  <div key={k} className="bg-cream-50 rounded p-2">
                    <div className="text-charcoal-800/50 uppercase tracking-wider text-[10px]">{k}</div>
                    <div className="font-mono text-charcoal-900 truncate">{String(v)}</div>
                  </div>
                ))}
              </div>
              {kiosk.system_info_at && (
                <p className="text-[10px] text-charcoal-800/40 mt-2">Captured {istDateTime(kiosk.system_info_at)}</p>
              )}
            </div>
          )}

          {/* Recent commands */}
          <div className="card-dashboard p-0 overflow-hidden">
            <div className="px-4 py-2 border-b border-charcoal-900/10 bg-cream-50 flex items-center justify-between">
              <h3 className="text-sm font-medium text-charcoal-800/80">Recent commands</h3>
              <span className="text-[10px] text-charcoal-800/40 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Live · refresh 3s
              </span>
            </div>
            {commands.length === 0 ? (
              <div className="text-center py-8 text-charcoal-800/40 text-sm">No commands yet</div>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm" data-testid="commands-table">
                  <thead className="bg-cream-100 sticky top-0">
                    <tr className="text-left text-xs text-charcoal-800/60">
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Command</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Issued by</th>
                      <th className="px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-900/5">
                    {commands.map(c => (
                      <tr key={c.id} className="hover:bg-cream-50">
                        <td className="px-3 py-2 text-xs text-charcoal-800/60 font-mono">
                          {istTime(c.issued_at)}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{c.cmd}</td>
                        <td className="px-3 py-2">
                          <CmdStatusBadge status={c.status} />
                        </td>
                        <td className="px-3 py-2 text-xs text-charcoal-800/70">{c.issued_by_username}</td>
                        <td className="px-3 py-2 text-xs text-charcoal-800/60 max-w-md truncate">
                          {c.error || (c.result ? JSON.stringify(c.result).slice(0, 80) : '—')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm command modal */}
      {showCmdConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCmdConfirm(null)}>
          <div className="bg-cream-50 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl mb-3">{showCmdConfirm.label}?</h2>
            <p className="text-sm text-charcoal-800/70 mb-5">{showCmdConfirm.body}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCmdConfirm(null)} className="px-4 py-2 text-charcoal-800/60">Cancel</button>
              <button
                onClick={() => sendCommand(showCmdConfirm.cmd, showCmdConfirm.args)}
                disabled={pendingCmd === showCmdConfirm.cmd}
                data-testid="cmd-confirm-btn"
                className={`px-4 py-2 rounded-lg text-cream-50 disabled:opacity-50 ${showCmdConfirm.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-charcoal-900 hover:bg-charcoal-800'}`}>
                {pendingCmd === showCmdConfirm.cmd ? 'Sending...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}



      {activeTab === 'appearance' && editUi && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          <div className="card-dashboard space-y-5">
            <h2 className="font-serif text-xl text-charcoal-900">Booth Software Theme</h2>
            <p className="text-sm text-charcoal-800/60 -mt-3">Live-syncs to the paired booth.</p>

            {[
              { key: 'background_color', label: 'Background', default: '#FDFCF8' },
              { key: 'accent_color', label: 'Accent (icons / highlights)', default: '#0F0F12' },
              { key: 'text_color', label: 'Text', default: '#1A1A1F' },
              { key: 'button_color', label: 'Primary button', default: '#0F0F12' },
            ].map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-charcoal-800 mb-2">{field.label}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editUi[field.key] || field.default}
                    onChange={(e) => setEditUi({ ...editUi, [field.key]: e.target.value })}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    data-testid={`${field.key}-color-input`}
                  />
                  <input
                    type="text"
                    value={editUi[field.key] || field.default}
                    onChange={(e) => setEditUi({ ...editUi, [field.key]: e.target.value })}
                    className="flex-1 px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl font-mono text-sm"
                    maxLength={9}
                  />
                </div>
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-charcoal-800 mb-2">Idle screen heading</label>
              <input
                type="text"
                value={editUi.idle_heading || ''}
                onChange={(e) => setEditUi({ ...editUi, idle_heading: e.target.value })}
                placeholder="e.g., Welcome to The Blue Bar"
                maxLength={80}
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl"
                data-testid="idle-heading-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-800 mb-2">Idle screen subheading</label>
              <input
                type="text"
                value={editUi.idle_subheading || ''}
                onChange={(e) => setEditUi({ ...editUi, idle_subheading: e.target.value })}
                placeholder="A short tagline"
                maxLength={120}
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl"
                data-testid="idle-subheading-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-800 mb-2">Booth brand logo</label>
              {editUi.idle_screen_logo_url ? (
                <div className="flex items-center gap-4 p-4 bg-cream-50 border border-charcoal-900/10 rounded-xl">
                  <img
                    src={editUi.idle_screen_logo_url}
                    alt="Brand logo"
                    className="h-14 max-w-[160px] object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    onClick={() => setEditUi({ ...editUi, idle_screen_logo_url: '' })}
                    className="ml-auto p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove logo"
                    data-testid="remove-idle-logo-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="block cursor-pointer">
                  <div className="border-2 border-dashed border-charcoal-900/20 rounded-xl p-6 text-center hover:border-charcoal-900/40 hover:bg-cream-50 transition-colors">
                    {logoUploading ? (
                      <div className="animate-pulse">
                        <div className="w-8 h-8 mx-auto mb-2 bg-charcoal-900/20 rounded-full" />
                        <span className="text-charcoal-800/60 text-sm">Uploading…</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-7 h-7 mx-auto mb-2 text-charcoal-800/40" />
                        <span className="text-charcoal-800/60 text-sm">Click to upload your logo</span>
                        <p className="text-xs text-charcoal-800/40 mt-1">PNG or JPG, up to 2MB</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={logoFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setLogoUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${API}/upload/logo?kiosk_id=${kioskId}`, fd, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                        });
                        setEditUi((prev) => ({ ...prev, idle_screen_logo_url: res.data.url }));
                      } catch (err) {
                        alert('Logo upload failed — please try again');
                      }
                      setLogoUploading(false);
                      if (logoFileRef.current) logoFileRef.current.value = '';
                    }}
                    data-testid="idle-logo-upload-input"
                  />
                </label>
              )}
              <p className="text-xs text-charcoal-800/40 mt-1">Shown on the booth's start screen. Falls back to the Piczoo logo when empty.</p>
              <details className="mt-2">
                <summary className="text-xs text-charcoal-800/40 cursor-pointer">Advanced: paste an image URL instead</summary>
                <input
                  type="url"
                  value={editUi.idle_screen_logo_url || ''}
                  onChange={(e) => setEditUi({ ...editUi, idle_screen_logo_url: e.target.value })}
                  placeholder="https://…"
                  maxLength={500}
                  className="mt-2 w-full px-4 py-2.5 bg-cream-50 border border-charcoal-900/10 rounded-xl text-sm"
                  data-testid="idle-logo-url-input"
                />
              </details>
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-800 mb-2">Logo alignment</label>
              <select
                value={editUi.logo_position || 'center'}
                onChange={(e) => setEditUi({ ...editUi, logo_position: e.target.value })}
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl"
                data-testid="logo-position-select"
              >
                <option value="top-left">Left</option>
                <option value="center">Center</option>
                <option value="top-right">Right</option>
              </select>
            </div>

            <button
              onClick={handleSaveUi}
              disabled={saving}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="save-appearance-btn"
            >
              {saving ? 'Saving...' : 'Save Appearance'}
            </button>
          </div>

          {/* Mini live preview */}
          <div className="card-dashboard">
            <h3 className="text-sm font-medium text-charcoal-900 mb-3">Live preview</h3>
            <div
              className="rounded-2xl border border-charcoal-900/10 overflow-hidden flex flex-col items-center justify-center text-center p-8 min-h-[420px]"
              style={{
                backgroundColor: editUi.background_color || '#FDFCF8',
                color: editUi.text_color || '#1A1A1F',
              }}
              data-testid="appearance-preview"
            >
              {editUi.idle_screen_logo_url ? (
                <img
                  src={editUi.idle_screen_logo_url}
                  alt="logo"
                  className={`max-h-16 mb-5 object-contain ${(editUi.logo_position || 'center').includes('left') ? 'self-start' : (editUi.logo_position || 'center').includes('right') ? 'self-end' : 'self-center'}`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <img src="/piczoo-logo.png" alt="Piczoo" className={`h-10 mb-5 ${(editUi.logo_position || 'center').includes('left') ? 'self-start' : (editUi.logo_position || 'center').includes('right') ? 'self-end' : 'self-center'}`} />
              )}
              <p className="font-serif text-xl mb-1">{editUi.idle_heading || (kiosk?.name || 'Welcome')}</p>
              {editUi.idle_subheading && (
                <p className="text-sm opacity-70 mb-6">{editUi.idle_subheading}</p>
              )}
              <button
                className="px-8 py-3 rounded-full text-white text-sm"
                style={{ backgroundColor: editUi.button_color || editUi.accent_color || '#0F0F12' }}
                disabled
              >
                Touch to Start
              </button>
            </div>
            <p className="text-xs text-charcoal-800/40 mt-3 text-center">
              Booths receive theme updates within 1–2 seconds via WebSocket.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="max-w-2xl">
          <h2 className="font-serif text-xl text-charcoal-900 mb-6">Kiosk Settings</h2>
          
          {/* Connection Status Card */}
          <div className="card-dashboard mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-charcoal-900">Connection Status</h3>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                connectionStatus?.connected_realtime 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {connectionStatus?.connected_realtime ? (
                  <>
                    <Wifi className="w-4 h-4" />
                    <span>Connected (Live)</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4" />
                    <span>Offline</span>
                  </>
                )}
              </div>
            </div>
            {connectionStatus?.last_seen && (
              <p className="text-charcoal-800/60 text-sm">
                Last seen: {istDateTime(connectionStatus.last_seen)}
              </p>
            )}
            {connectionStatus?.camera_mode && (
              <p className={`text-sm mt-2 flex items-center gap-1 ${connectionStatus.camera_mode === 'native' ? 'text-amber-600' : 'text-charcoal-800/60'}`} data-testid="connection-camera-mode">
                <Camera className="w-3 h-3" />
                Camera engine: {connectionStatus.camera_mode === 'native' ? 'Native (ffmpeg) — WebRTC bypassed' : 'Standard (Chromium)'}
                {connectionStatus.app_version ? ` · v${connectionStatus.app_version}` : ''}
              </p>
            )}
            {connectionStatus?.connected_realtime && (
              <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                Real-time sync active - changes sync instantly
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Admin Password */}
            <div className="card-dashboard">
              <div className="flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-charcoal-800/60" />
                <h3 className="font-medium text-charcoal-900">Admin Password</h3>
              </div>
              <p className="text-charcoal-800/60 text-sm mb-3">Required to exit kiosk mode</p>
              <input
                type="text"
                value={kiosk.admin_password}
                readOnly
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl font-mono text-sm mb-3"
              />
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full px-4 py-2 border border-charcoal-900/20 text-charcoal-900 rounded-xl hover:bg-cream-50 transition-colors flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                Change Password
              </button>
              {connectionStatus?.connected_realtime && (
                <p className="text-xs text-green-600 mt-2 text-center">Password will sync instantly</p>
              )}
            </div>

            {/* Force Disconnect */}
            <div className="card-dashboard">
              <div className="flex items-center gap-2 mb-4">
                <Power className="w-5 h-5 text-red-500" />
                <h3 className="font-medium text-charcoal-900">Remote Control</h3>
              </div>
              <p className="text-charcoal-800/60 text-sm mb-3">
                Force disconnect the kiosk. It will require re-pairing.
              </p>
              <button
                onClick={() => setShowDisconnectModal(true)}
                disabled={!connectionStatus?.connected_realtime}
                className="w-full px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Power className="w-4 h-4" />
                Force Disconnect
              </button>
              {!connectionStatus?.connected_realtime && (
                <p className="text-xs text-charcoal-800/40 mt-2 text-center">Kiosk must be online</p>
              )}
            </div>
          </div>

          {/* Kiosk App Download */}
          <div className="card-dashboard mt-6">
            <h3 className="font-medium text-charcoal-900 mb-2">Kiosk App Download</h3>
            <p className="text-charcoal-800/60 text-sm mb-4">
              Download the Windows kiosk application to install on your photobooth PC.
            </p>
            <button className="w-full px-4 py-3 bg-charcoal-900 text-white rounded-xl flex items-center justify-center gap-2 hover:bg-charcoal-800 transition-colors">
              <Download className="w-4 h-4" />
              Download Kiosk App (Windows)
            </button>
            <p className="text-xs text-charcoal-800/40 mt-2 text-center">Version 2.0 • Windows 10+</p>
          </div>

          {/* Danger Zone */}
          {user?.role === 'super_admin' && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50/40 p-5 sm:p-6" data-testid="danger-zone">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900">Danger Zone</h3>
                  <p className="text-red-900/70 text-sm mt-1">
                    Deleting this kiosk will permanently remove all its data — pairing code, pricing, inventory, print logs, photo sessions, and orders. This cannot be undone.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full sm:w-auto px-4 py-2.5 bg-white text-red-700 border border-red-300 rounded-xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2 font-medium"
                data-testid="open-delete-kiosk-modal-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete Kiosk Permanently
              </button>
            </div>
          )}
        </div>
      )}

      {/* Force Disconnect Modal */}
      {showDisconnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Power className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-serif text-2xl text-charcoal-900">Force Disconnect?</h3>
            </div>
            <p className="text-charcoal-800/60 mb-6">
              This will immediately disconnect the kiosk and generate a new pairing code. The kiosk will show a disconnected screen and require re-pairing.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisconnectModal(false)}
                className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleForceDisconnect}
                disabled={disconnecting}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {disconnecting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-serif text-2xl text-charcoal-900">Change Password</h3>
            </div>
            <p className="text-charcoal-800/60 mb-4">
              Enter a new admin password. {connectionStatus?.connected_realtime ? 'It will sync to the kiosk instantly.' : 'It will sync when the kiosk reconnects.'}
            </p>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              minLength={6}
              maxLength={64}
              className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl mb-4"
              data-testid="new-admin-password-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}
                className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={passwordSaving || newPassword.length < 6}
                className="flex-1 px-4 py-3 bg-charcoal-900 text-white rounded-xl hover:bg-charcoal-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {passwordSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Pairing Code Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" data-testid="regenerate-modal">
            <h3 className="font-serif text-2xl text-charcoal-900 mb-4">Regenerate Pairing Code?</h3>
            <p className="text-charcoal-800/60 mb-6">
              This will disconnect the currently paired kiosk. You'll need to re-pair it with the new code.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                data-testid="cancel-regenerate-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleRegeneratePairingCode}
                disabled={regenerating}
                className="flex-1 px-4 py-3 bg-rose-500 text-white rounded-xl hover:bg-rose-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-regenerate-btn"
              >
                {regenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  'Regenerate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Adjustment Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/60 backdrop-blur-sm" data-testid="adjust-inventory-modal">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
            <h3 className="font-serif text-2xl text-charcoal-900 mb-1">Inventory Adjustment</h3>
            <p className="text-sm text-charcoal-800/60 mb-5">
              {adjustType === 'waste' && 'Record a wasted print (counts against the 700-print roll).'}
              {adjustType === 'multi_print' && 'Record an extra copy printed (e.g., reprint, request).'}
              {adjustType === 'manual_decrement' && 'Manually subtract prints from the remaining count.'}
              {adjustType === 'manual_increment' && 'Add prints back (e.g., to correct an overcount).'}
            </p>

            <label className="block text-sm font-medium text-charcoal-800 mb-2">Type</label>
            <select
              value={adjustType}
              onChange={(e) => setAdjustType(e.target.value)}
              className="w-full px-4 py-3 mb-4 bg-cream-50 border border-charcoal-900/10 rounded-xl"
              data-testid="adjust-type-select"
            >
              <option value="waste">Wasted print</option>
              <option value="multi_print">Extra copy</option>
              <option value="manual_decrement">Manual decrement</option>
              <option value="manual_increment">Manual increment</option>
            </select>

            <label className="block text-sm font-medium text-charcoal-800 mb-2">Count</label>
            <input
              type="number"
              min={1}
              max={50}
              value={adjustDelta}
              onChange={(e) => setAdjustDelta(e.target.value)}
              className="w-full px-4 py-3 mb-4 bg-cream-50 border border-charcoal-900/10 rounded-xl"
              data-testid="adjust-delta-input"
            />

            <label className="block text-sm font-medium text-charcoal-800 mb-2">Reason (optional)</label>
            <input
              type="text"
              maxLength={200}
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="e.g., misalignment, ink streak, customer reprint"
              className="w-full px-4 py-3 mb-5 bg-cream-50 border border-charcoal-900/10 rounded-xl"
              data-testid="adjust-reason-input"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowAdjustModal(false)}
                disabled={savingAdjust}
                className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitAdjustment}
                disabled={savingAdjust || !adjustDelta}
                className="flex-1 px-4 py-3 bg-charcoal-900 text-white rounded-xl hover:bg-charcoal-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-adjust-btn"
              >
                {savingAdjust ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingAdjust ? 'Saving...' : 'Save adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Kiosk - 3-Step Confirmation Modal */}      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/70 backdrop-blur-sm"
          data-testid="delete-kiosk-modal"
        >
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 w-12 rounded-full transition-colors ${
                    s <= deleteStep ? 'bg-red-500' : 'bg-charcoal-900/10'
                  }`}
                  data-testid={`delete-step-indicator-${s}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-red-600 font-medium">Step {deleteStep} of 3</p>
                <h3 className="font-serif text-xl sm:text-2xl text-charcoal-900">
                  {deleteStep === 1 && 'Delete this kiosk?'}
                  {deleteStep === 2 && 'Confirm the kiosk name'}
                  {deleteStep === 3 && 'Final confirmation'}
                </h3>
              </div>
            </div>

            {/* Step 1: warning + acknowledgement */}
            {deleteStep === 1 && (
              <div data-testid="delete-step-1">
                <p className="text-charcoal-800/70 text-sm mb-4">
                  You're about to permanently delete <span className="font-medium text-charcoal-900">"{kiosk.name}"</span>.
                  The following data will be removed forever:
                </p>
                <ul className="text-sm text-charcoal-800/70 space-y-1 mb-5 pl-5 list-disc">
                  <li>Pairing code &amp; live connection</li>
                  <li>Pricing &amp; template configuration</li>
                  <li>Paper inventory record</li>
                  <li>All print logs &amp; print history</li>
                  <li>All photo sessions (download links will break)</li>
                  <li>All payment orders for this kiosk</li>
                </ul>
                <label className="flex items-start gap-3 p-3 rounded-xl border border-charcoal-900/10 cursor-pointer hover:bg-cream-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={deleteAck}
                    onChange={(e) => setDeleteAck(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-red-600"
                    data-testid="delete-ack-checkbox"
                  />
                  <span className="text-sm text-charcoal-900">
                    I understand this action is permanent and cannot be undone.
                  </span>
                </label>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={resetDeleteFlow}
                    className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                    data-testid="delete-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setDeleteStep(2)}
                    disabled={!deleteAck}
                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="delete-step-1-next-btn"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: type kiosk name */}
            {deleteStep === 2 && (
              <div data-testid="delete-step-2">
                <p className="text-charcoal-800/70 text-sm mb-3">
                  To confirm, type the kiosk's exact name:
                </p>
                <p className="font-mono text-charcoal-900 bg-cream-50 border border-charcoal-900/10 rounded-lg px-3 py-2 mb-3 text-sm select-none">
                  {kiosk.name}
                </p>
                <input
                  type="text"
                  value={deleteNameInput}
                  onChange={(e) => setDeleteNameInput(e.target.value)}
                  placeholder="Type kiosk name here"
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white border border-charcoal-900/15 rounded-xl focus:outline-none focus:border-red-400 transition-colors"
                  data-testid="delete-name-input"
                />
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setDeleteStep(1)}
                    className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors"
                    data-testid="delete-step-2-back-btn"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setDeleteStep(3)}
                    disabled={deleteNameInput.trim() !== kiosk.name}
                    className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="delete-step-2-next-btn"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: type DELETE */}
            {deleteStep === 3 && (
              <div data-testid="delete-step-3">
                <p className="text-charcoal-800/70 text-sm mb-3">
                  Last step. Type <span className="font-mono font-bold text-red-600">DELETE</span> in capital letters to permanently remove this kiosk.
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white border border-charcoal-900/15 rounded-xl focus:outline-none focus:border-red-400 transition-colors font-mono"
                  data-testid="delete-confirm-input"
                />
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setDeleteStep(2)}
                    disabled={deleting}
                    className="flex-1 px-4 py-3 border border-charcoal-900/10 rounded-xl hover:bg-cream-50 transition-colors disabled:opacity-50"
                    data-testid="delete-step-3-back-btn"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDeleteKiosk}
                    disabled={deleting || deleteConfirmText !== 'DELETE'}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                    data-testid="delete-confirm-btn"
                  >
                    {deleting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete Forever
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ConsoleButton = ({ icon, label, onClick, testid, superOnly }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="flex items-center gap-2 px-3 py-2 border border-charcoal-900/10 rounded-lg hover:bg-cream-50 text-sm text-left transition-colors"
  >
    <span className="text-lg">{icon}</span>
    <span className="flex-1">{label}</span>
    {superOnly && <span className="text-[9px] uppercase text-charcoal-800/40">super</span>}
  </button>
);

const CmdStatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-blue-100 text-blue-700',
    delivered: 'bg-amber-100 text-amber-800',
    completed: 'bg-green-100 text-green-700',
    errored: 'bg-red-100 text-red-700',
    expired: 'bg-gray-200 text-gray-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${map[status] || 'bg-gray-100'}`}>{status}</span>;
};

/**
 * Autostart-on-Windows toggle with desired-vs-reported drift indicator.
 *
 * Model: PATCH /api/kiosks/{id}/autostart updates `autostart_desired`
 * immediately AND queues a signed command via the booth-command bus. The
 * booth ACKs its actual applied state via /command-results, which sets
 * `autostart_reported`. If the two disagree (e.g. booth is offline), the
 * badge shows PENDING; the command is queued and delivered on next
 * booth connect/poll — no data loss.
 */
const AutostartToggleCard = ({ kiosk, kioskId, token, onApplied }) => {
  const desired = kiosk?.autostart_desired;
  const reported = kiosk?.autostart_reported;
  const reportedAt = kiosk?.autostart_reported_at;
  // If nobody has ever set desired, treat reported as the source of truth
  // for what the toggle currently shows. If both are null, default to
  // "unknown" and let the operator flip it explicitly.
  const uiValue =
    typeof desired === 'boolean' ? desired
    : typeof reported === 'boolean' ? reported
    : false;

  const [saving, setSaving] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(uiValue);

  React.useEffect(() => {
    setLocalValue(uiValue);
  }, [uiValue]);

  const flip = async () => {
    const next = !localValue;
    setSaving(true);
    setLocalValue(next); // optimistic
    try {
      await axios.patch(
        `${API}/kiosks/${kioskId}/autostart`,
        { enabled: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onApplied) await onApplied();
    } catch (err) {
      setLocalValue(!next); // revert
      const msg = err.response?.data?.detail || 'Failed to update autostart';
      import('sonner').then(({ toast }) => toast.error(msg));
    }
    setSaving(false);
  };

  // Drift / status badge — three cases: applied, pending (booth offline or
  // hasn't acked yet), or never confirmed.
  let badge;
  if (typeof desired !== 'boolean' && typeof reported !== 'boolean') {
    badge = { text: 'Not set', cls: 'bg-gray-100 text-gray-500' };
  } else if (typeof reported !== 'boolean') {
    badge = { text: 'Pending — booth hasn’t reported', cls: 'bg-amber-100 text-amber-800' };
  } else if (typeof desired === 'boolean' && desired !== reported) {
    badge = { text: 'Pending — booth offline / queued', cls: 'bg-amber-100 text-amber-800' };
  } else {
    badge = { text: 'Applied', cls: 'bg-green-100 text-green-700' };
  }

  return (
    <div className="card-dashboard" data-testid="autostart-toggle-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="text-sm font-medium text-charcoal-800/80">
            Start automatically on Windows boot
          </h3>
          <p className="text-xs text-charcoal-800/55 mt-1 max-w-md">
            When ON, the booth software launches itself the moment the Windows
            user signs in. Change here reaches the booth even if it’s offline —
            it will apply on the booth’s next connection.
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span
              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${badge.cls}`}
              data-testid="autostart-badge"
            >
              {badge.text}
            </span>
            {typeof reported === 'boolean' && (
              <span className="text-[11px] text-charcoal-800/50">
                Booth reports:{' '}
                <span className="font-medium text-charcoal-800/80">
                  {reported ? 'ON' : 'OFF'}
                </span>
                {reportedAt && (
                  <span className="text-charcoal-800/40">
                    {' '}· {istDateTime(reportedAt)}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Toggle switch — reachable via keyboard, has explicit test id */}
        <button
          type="button"
          role="switch"
          aria-checked={localValue}
          aria-label="Toggle Windows autostart"
          onClick={flip}
          disabled={saving}
          data-testid="autostart-toggle"
          data-value={localValue ? 'on' : 'off'}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
            localValue ? 'bg-piczoo-500' : 'bg-charcoal-900/20'
          } ${saving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              localValue ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default KioskDetail;
